import React, { useState } from "react";

import { mdiMore } from "@mdi/js";
import Icon from "@mdi/react";
import { useTranslation } from "react-i18next";

import Button from "../button/Button";
import AdsParamsModal from "../content/AdsParamsModal";

import type { JSX } from "react";

import type { NewURL, NewURLSearchParams } from "../../types/url";

interface AdParamsButtonProps {
  /** VAST URL whose query string is inspected. */
  url: string | null;
}

/**
 * Reveals the query parameters of a VAST URL in a modal.
 *
 * Shared by every VAST URL field of the advertising form. The button stays
 * mounted but disabled while the URL carries no parameters, so it does not
 * appear and disappear under the operator as they type.
 */
function AdParamsButton({ url }: AdParamsButtonProps): JSX.Element {
  const { t } = useTranslation("editor-content");
  const [isParamsOpen, setIsParamsOpen] = useState(false);

  // URL.parse is not available in this TS version, hence the canParse cast.
  const contentURL =
    url && (URL as unknown as NewURL).canParse(url) ? new URL(url) : null;

  const searchParams =
    contentURL &&
    (new URLSearchParams(contentURL.search) as unknown as NewURLSearchParams);

  return (
    <React.Fragment>
      <div className="flex">
        <Button
          className="enabled:text-primary enabled:bg-card font-semibold"
          isDisabled={!searchParams?.size}
          onClick={(): void => setIsParamsOpen(true)}
          size="xs"
          type="button"
          variant="secondary"
        >
          <Icon path={mdiMore} size={0.7} />
          {t("showAdParams")}
        </Button>
      </div>
      {searchParams ? (
        <AdsParamsModal
          isOpen={isParamsOpen}
          onClose={(): void => setIsParamsOpen(false)}
          searchParams={searchParams}
        />
      ) : null}
    </React.Fragment>
  );
}

export default AdParamsButton;
